'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Kombucha: Brewing Vessels, Bottles & SCOBYs"
      author="MicroPlastics Editorial Team"
      date="May 7, 2026"
      readTime="8 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in kombucha from brewing vessels and bottles"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Most commercial kombucha ships in glass, but the <strong>plastic-lined
        bottle cap</strong>, plastic <strong>home-brewing vessels</strong>, and
        <strong> acidic fermentation</strong> (typical kombucha pH 2.5–3.5)
        accelerate microplastic leaching from any plastic surface it contacts.
        For commercial brands, GT&apos;s Synergy, Health-Ade, Brew Dr., and Better
        Booch all use glass bottles with metal caps. For home brewers, choose
        a glass or food-grade ceramic vessel and a glass secondary bottle with
        flip-top metal closure.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Kombucha&apos;s low pH (2.5–3.5) is more acidic than coffee or orange juice, it accelerates leaching from any plastic surface.</>,
          <>Commercial kombucha in glass bottles is generally clean; check the cap for plastic interior lining.</>,
          <>Home-brewers using plastic fermentation vessels (PET, polypropylene) are exposing the brew to weeks of acidic contact.</>,
          <>The SCOBY (symbiotic culture of bacteria and yeast) does not break down microplastics, it just creates more contact time.</>,
          <>Secondary fermentation in plastic flip-top bottles (popular home brewer choice) adds another exposure layer; use glass.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why kombucha is a higher-risk fermented drink for plastic
      </h2>
      <p>
        Kombucha&apos;s typical pH of 2.5–3.5 is similar to lemon juice or
        stomach acid. That acidity is part of what makes kombucha shelf-stable
        and refreshing, but it also accelerates the breakdown of any plastic
        surface the brew contacts. Add the 7–30 day fermentation time and you
        have a perfect plastic-leaching scenario if the brewing vessel or
        secondary-fermentation bottle is plastic.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Commercial kombucha: mostly clean (with one catch)
      </h2>
      <p>
        Major brands almost universally ship in glass. GT&apos;s, Health-Ade,
        Brew Dr., Better Booch, Humm, Wild Kombucha. The bottle body is inert.
        The catch is the cap: most metal screw caps have a plastic or
        plasticised-rubber inner liner that contacts the kombucha across the
        cap&apos;s surface. For continuously fizzy kombucha, that contact
        pressure can be meaningful over months in storage.
      </p>
      <p>
        A few brands and many small craft brewers use a swing-top flip cap with
        a ceramic top and rubber gasket, the gasket is typically silicone,
        which is less of a concern than thermoplastic.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Home-brewing pitfalls
      </h2>
      <p>
        Home brewing is great for cost and creativity but creates several plastic
        exposure points if you&apos;re not careful:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Plastic primary fermenter.</strong> 1-gallon and 2-gallon PET or
          PP &ldquo;continuous brew&rdquo; jars are popular and cheap, but expose
          the kombucha to acidic plastic contact for 7–14 days.
        </li>
        <li>
          <strong>Plastic flip-top secondary bottles.</strong> Some &ldquo;BPA-free&rdquo;
          PET secondary bottles are sold as kombucha-specific because they
          flex slightly under pressure (reducing explosion risk). They also leach.
        </li>
        <li>
          <strong>Plastic spigots.</strong> Continuous-brew jars often come with
          a PP plastic spigot at the bottom. Constant contact + acidity.
        </li>
        <li>
          <strong>Plastic funnels and tubing</strong> used to transfer between
          vessels.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Kombucha formats compared
      </h2>

      <BlogComparisonTable
        caption="Kombucha brewing and packaging by microplastic exposure"
        columns={[
          { key: 'format', label: 'Source' },
          { key: 'risk', label: 'Relative exposure' },
          { key: 'why', label: 'Why' },
        ]}
        rows={[
          { format: 'Home brew in glass vessel, glass swing-top bottles', risk: 'Lowest', why: 'All glass + silicone gasket contact' },
          { format: 'Commercial glass-bottled kombucha (GT\'s, Health-Ade, etc.)', risk: 'Low', why: 'Glass body + cap liner only' },
          { format: 'Tap kombucha in your own glass growler', risk: 'Low', why: 'Glass storage; minimal plastic contact' },
          { format: 'Plastic-bottled kombucha (rare, mostly some store brands)', risk: 'Higher', why: 'PET in direct acidic contact for months' },
          { format: 'Home brew in plastic vessel with plastic spigot', risk: 'Higher', why: 'Acidic, long contact time, plastic flexing' },
          { format: 'Tap kombucha in plastic to-go cup', risk: 'Higher', why: 'PE-lined cup + plastic lid + acidic kombucha' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical home-brew setup (low-microplastic)
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Primary fermenter:</strong> 1-gallon or 2-gallon glass jar with breathable cotton cloth lid (not plastic wrap).</li>
        <li><strong>SCOBY hotel:</strong> separate glass jar with kombucha starter liquid.</li>
        <li><strong>Stirring:</strong> wooden or stainless-steel spoon.</li>
        <li><strong>Secondary fermentation:</strong> glass swing-top flip bottles (Grolsch-style) with silicone gasket.</li>
        <li><strong>Funnel:</strong> stainless-steel or silicone.</li>
        <li><strong>Strainer:</strong> stainless mesh, not plastic mesh.</li>
        <li><strong>Continuous-brew vessel:</strong> if going this route, choose food-grade ceramic or all-glass with a stainless-steel spigot, not plastic.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-beer-and-wine" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in beer and wine
        </Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-soda" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in soda
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Does kombucha contain microplastics?',
            answer:
              'Commercial glass-bottled kombucha is largely clean, with only cap-liner contact. Home-brewed kombucha in plastic vessels has higher exposure because kombucha\'s low pH (2.5–3.5) accelerates leaching from any plastic surface over the 7–30 day fermentation.',
          },
          {
            question: 'Are GT\'s and Health-Ade kombucha safe for microplastics?',
            answer:
              'Yes, both ship in glass bottles. The metal screw caps have a plastic inner liner that contacts the brew, but this is far less exposure than plastic bottling. They are among the cleaner commercial kombucha options.',
          },
          {
            question: 'Can I brew kombucha in plastic?',
            answer:
              'You can, but kombucha\'s acidity accelerates microplastic leaching from any plastic vessel. Glass primary fermenter, glass swing-top secondary bottles, and a stainless or wooden stirring tool eliminate the largest exposure sources.',
          },
          {
            question: 'Does the SCOBY filter out microplastics?',
            answer:
              'No. The symbiotic culture of bacteria and yeast (SCOBY) does not break down or filter microplastic particles. If anything, longer fermentation in contact with plastic creates more exposure.',
          },
          {
            question: 'Is bottled kombucha better than tap kombucha?',
            answer:
              'It depends on the container. Glass-bottled kombucha is generally cleaner than tap kombucha served in a plastic to-go cup. Tap kombucha in your own glass growler is the cleanest option of all.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Diaz-Basantes MF, Conesa JA, Fullana A',
            year: 2020,
            title: 'Microplastics in honey, beer, milk and refreshments in Ecuador',
            publisher: 'Sustainability',
            url: 'https://www.mdpi.com/2071-1050/12/14/5514',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the release of microplastics from plastic containers and reusable food pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Zangmeister CD, Radney JG, Benkstein KD, Kalanyan B',
            year: 2022,
            title: 'Common single-use consumer plastic products release trillions of submicron microparticles',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.2c00942',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2016.4501',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
